/**
 * Media Quarantine — Coming Soon holding state.
 *
 * Doctrine (May 25, 2026):
 *   • Calm, honest, no urgency.
 *   • Tell the user what's offline, what's safe, and what comes next.
 *   • No CTAs to broken flows. No "join the waitlist" growth-hack copy.
 *   • Optional admin diagnostic link visible only to admins.
 *
 * Props:
 *   • `title` — defaults to "Video analysis coming soon".
 *   • `area`  — optional area label (e.g. "Lesson review",
 *               "Upload lab"). Used in the body copy.
 *   • `isAdmin` — if true, render a subtle admin-bypass banner.
 *   • `adminContinueTo` — optional href for the admin to bypass into
 *                         the underlying page (used by route wrapper).
 */
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Info } from "lucide-react";

export default function MediaComingSoon({
  title = "Video analysis coming soon",
  area = "Media systems",
  isAdmin = false,
  adminContinueTo = null,
}) {
  return (
    <div
      data-testid="media-quarantine-coming-soon"
      className="min-h-dvh bg-slate-50 dark:bg-slate-950 flex items-start justify-center px-4 py-12"
    >
      <div className="w-full max-w-md mx-auto">
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm p-6">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-4">
            <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
          </div>
          <h1 className="text-xl font-extrabold text-slate-900 dark:text-white leading-tight">
            {title}
          </h1>
          <p className="mt-2 text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">
            <strong>{area}</strong> are temporarily offline while we move them
            onto our new isolated media infrastructure. Your historical data
            is safe — new uploads and reviews will return cleanly when the
            bridge is complete.
          </p>

          <div
            data-testid="media-quarantine-status-block"
            className="mt-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 px-3 py-2.5 text-[12px] text-slate-700 dark:text-slate-300"
          >
            <p className="flex items-center gap-1.5 font-bold uppercase tracking-widest text-[10px] text-slate-500 mb-1">
              <Info className="w-3 h-3" /> Status
            </p>
            <p className="leading-snug">Media systems: temporarily offline.</p>
            <p className="leading-snug">Existing historical data: safe and untouched.</p>
            <p className="leading-snug">New uploads/reviews: unavailable from this route.</p>
          </div>

          {isAdmin && (
            <div
              data-testid="media-quarantine-admin-banner"
              className="mt-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-[12px] text-amber-900 dark:text-amber-200"
            >
              <p className="flex items-center gap-1.5 font-bold mb-0.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Admin bypass available
              </p>
              <p className="leading-snug">
                You're an admin. The underlying page is still wired internally
                for diagnostic verification.
              </p>
              {adminContinueTo && (
                <Link
                  to={adminContinueTo}
                  data-testid="media-quarantine-admin-continue"
                  className="mt-1.5 inline-flex items-center gap-1 text-amber-900 dark:text-amber-200 underline font-bold text-[11.5px]"
                >
                  Continue to the underlying page →
                </Link>
              )}
            </div>
          )}

          <div className="mt-5 flex items-center gap-2">
            <Link
              to="/"
              data-testid="media-quarantine-home-link"
              className="px-3 py-1.5 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[12.5px] font-bold hover:opacity-90 transition"
            >
              Back to home
            </Link>
          </div>
        </div>
        <p className="mt-3 text-center text-[10.5px] text-slate-400">
          National Driving Guild · operational platform unaffected
        </p>
      </div>
    </div>
  );
}
